export type WhatsNewItem = {
  id: string;
  date: string;
  title: string;
  summary: string;
  details: string[];
  area: "Frontend" | "Backend" | "Data" | "Platform";
};

export const whatsNewItems: WhatsNewItem[] = [
  {
    id: "2026-10-21-resource-health-snapshot-email",
    date: "2026-10-21",
    title: "Email your Resource Health snapshot from the demo",
    summary:
      "Take your Resource Health summary with you: demo visitors can request an email instead of downloading files to the shared computer.",
    details: [
      "Email me my snapshot sits beside Start over and replaces the resource-table Export buttons.",
      "Confirm the selected person's work address to queue the displayed snapshot summary or comparison and owned-resource details.",
      "Emailed resource tables focus on linked resource names and missing metadata/review details; Type, Health, and Storage remain in the on-screen tables.",
      "Emails use the existing branded mail queue and standard system copies. Queued status confirms submission, not delivery.",
      "Only authorized demo hosts can submit emails; recipients are matched to snapshot data and repeat submissions have a five-minute cooldown.",
      "Owned-resource tables now show Teams icons for TeamSite and Outlook icons for GroupConnectedSite, with accessible labels and tooltips.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-21-resource-health-demo",
    date: "2026-10-21",
    title: "Resource Health walk-up demo added",
    summary:
      "Explore a person's historical Resource Health with snapshot comparisons, visual summaries, and owned-resource details.",
    details: [
      "Authorized demo hosts can search for a person, compare their latest two active snapshots, or view an individual snapshot.",
      "Summary cards, a health-score ring, and paired comparison bars highlight health, resource counts, storage, and resources needing attention.",
      "Side-by-side owned-resource lists are sorted by health. Email me my snapshot now replaces the original per-snapshot CSV exports.",
      "The focused demo page hides the sidebar and breadcrumbs. Start over clears the selected person and returns to search.",
      "The dedicated ResourceHealthDemo role enables person summary and owned-resource lookup without granting SuperAdmin access. Ordinary users' person-access rules remain unchanged.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-21-sharepoint-crawl-health",
    date: "2026-10-21",
    title: "SharePoint Inventory crawl monitoring improved",
    summary:
      "Find crawl runs more easily with site search, sortable columns, latest-activity ordering, and total site storage.",
    details: [
      "Crawl Health defaults to latest activity first, bringing resumed crawls back toward the top after a refresh.",
      "Search crawl history by site title or URL, combined with SharePoint/OneDrive scope and status filters.",
      "Sort across all result pages by site, status, stage, file and library counts, timestamps, or Total Storage.",
      "Total Storage shows the latest recorded SharePoint site usage, including version history, rather than the files processed by a crawl. Unavailable measurements display as Unknown.",
      "A wider scope selector and compact timestamps make the crawl table easier to scan.",
      "Inventory file lists, summaries, charts, and exports now exclude files marked as deleted by the importer.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-19-physical-records-categories-search",
    date: "2026-10-19",
    title: "Physical Records categories and direct record navigation added",
    summary:
      "Review imported category properties on boxes and files, find records using part of a physical ID, and open record details directly from search results.",
    details: [
      "The Categories tab presents imported attributes as compact, read-only labeled values, preserving leading zeros and repeated entries.",
      "Import details retain source identifiers, typed values, and assignment versions for reference.",
      "Physical Records administrators can browse category and attribute definitions.",
      "Physical ID searches support partial matches for file and box numbers.",
      "Search results open the dedicated record page. Shorter record URLs remove the Objects breadcrumb, and existing links redirect while retaining the selected tab.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-09-oob-artifact-downloads",
    date: "2026-10-09",
    title: "Out-of-Band artifact downloads use authenticated access",
    summary:
      "OOB artifact library downloads now use the authenticated portal download flow.",
    details: [
      "Artifact downloads use portal authentication instead of relying on a direct file link.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-07-oob-participant-administration",
    date: "2026-10-07",
    title: "Out-of-Band participant administration improved",
    summary:
      "Find existing participants or look up employees when adding members to OOB groups.",
    details: [
      "Search existing participants before adding them to a group.",
      "Use employee-directory lookup when creating a new group member, with duplicate prevention.",
      "Local-event administrator previews have been updated.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-06-oob-local-event-workflows",
    date: "2026-10-06",
    title: "Out-of-Band events now support local delivery",
    summary:
      "Create and manage events for local participants without provisioning a secondary-tenant Team, with clearer event navigation and participant chat updates.",
    details: [
      "Local events no longer run secondary-tenant Team setup or cleanup steps.",
      "Manage Events identifies whether an event uses local or secondary-tenant delivery.",
      "Active-event navigation and local participant chat have been refined, including message refresh.",
      "Teams-ready events now show only the tabs relevant to their current state.",
    ],
    area: "Platform",
  },
  {
    id: "2026-10-05-physical-records-detail-workspace",
    date: "2026-10-05",
    title: "Physical Records details and audit views improved",
    summary:
      "The Physical Records workspace now makes object details, relationships, and audit activity easier to review and manage.",
    details: [
      "Object details are organized into focused tabs for general information, contents, audit, and migration details.",
      "Edit record information and manage box/file relationships through focused workflows.",
      "Resize or collapse the details workspace and move between related records more smoothly.",
      "Responsive detail cards, record identifiers, and audit event icons make record information easier to scan.",
    ],
    area: "Platform",
  },
  {
    id: "2026-09-25-physical-records-folders-audit",
    date: "2026-09-25",
    title: "Physical Records folder management and audit tab added",
    summary:
      "Manage subfolders from the Physical Records browser and review available record audit history in a dedicated tab.",
    details: [
      "Create and delete subfolders with validation and confirmation.",
      "The top-level Physical folder is hidden from directory navigation.",
      "A dedicated Audit tab displays record history where audit data is available.",
    ],
    area: "Platform",
  },
  {
    id: "2026-09-24-my-groups",
    date: "2026-09-24",
    title: "My Groups experience released",
    summary:
      "Browse and manage your Microsoft 365 groups from the dedicated My Groups experience.",
    details: [
      "Provides a focused place for users to review the groups they belong to.",
      "The experience is available as a dedicated release in the portal.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-09-16-email-filing-workflow",
    date: "2026-09-16",
    title: "Email Filing workflow added",
    summary:
      "File email through the portal by choosing a SharePoint destination and submitting it through the integrated workflow.",
    details: [
      "Choose a SharePoint destination using the destination picker.",
      "The workflow is connected to the portal API and uses the required portal sign-in permission.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-09-15-oob-event-lifecycle",
    date: "2026-09-15",
    title: "Out-of-Band event lifecycle and local event support improved",
    summary:
      "Event administrators can manage event delivery, channels, questions, and closeout through a clearer lifecycle workflow.",
    details: [
      "Added local event delivery support for event channels and questions.",
      "Refined the administration board, event lifecycle order, and closeout tabs.",
    ],
    area: "Platform",
  },
  {
    id: "2026-09-10-resource-metadata-editing",
    date: "2026-09-10",
    title: "Resource metadata editing refined",
    summary:
      "Resource editors now have clearer Business Unit and Cost Center/Department ID controls, with saved changes reflected immediately.",
    details: [
      "Business Unit uses a fixed dropdown, while Cost Center and Department ID offer suggestions and allow manual entry.",
      "Resource lists and open detail tabs stay in sync after metadata is saved without requiring a full refresh.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-09-08-sharepoint-file-inventory",
    date: "2026-09-08",
    title: "SharePoint File Inventory released for admins",
    summary:
      "Admins can review inventoried SharePoint files, explore libraries, and monitor crawl health from a dedicated inventory workspace.",
    details: [
      "Review inventory coverage, file and storage summaries, and sortable site information.",
      "Explore files across the tenant or within a selected site, filter results, and export to CSV.",
      "Inspect site libraries, versioning configuration, crawl history, and library errors.",
      "Choose SharePoint, OneDrive, or combined inventory scope where available.",
    ],
    area: "Platform",
  },
  {
    id: "2026-08-31-physical-records-module",
    date: "2026-08-31",
    title: "New Physical Records module for tracking boxes and files",
    summary:
      "A new Physical Records solution lets admins and department users create, search, and manage physical box and file records, complete with retention labels, requests/transfers, and disposition reporting.",
    details: [
      "Create and edit boxes and files with auto-generated numbers, retention labels, and Business Unit/Department scoping.",
      "Search boxes and files by number, name, keywords, dates, and more, with Excel export and Code128 barcode label printing.",
      "Request a box and have admins check it out/return it, with a lightweight audit trail and email notification to the admin inbox.",
      "Admin screens to manage Business Units, Departments, Locations, Retention Labels, and user access assignments.",
      "Admin-only Box Disposition Report with eligibility calculation, Certificate of Destruction upload, and a generic CSV import tool for migrating legacy records.",
    ],
    area: "Platform",
  },
  {
    id: "2026-08-25-oob-secondary-tenant-support",
    date: "2026-08-25",
    title: "Out-of-Band now supports secondary tenant team provisioning",
    summary:
      "OOB events can now provision and manage Microsoft Teams in a secondary tenant, with onboarding and communication paths tuned for that scenario.",
    details: [
      "Added secondary tenant team provisioning, including team creation and membership reconciliation.",
      "Onboarding links now enforce Microsoft sign-in instead of routing through an external flow.",
      "Participant communications and status updates now route to personal email for secondary tenant scenarios.",
      "Event status views can now be reviewed across all lifecycle states, not just active ones.",
    ],
    area: "Platform",
  },
  {
    id: "2026-08-21-user-admin-employee-directories",
    date: "2026-08-21",
    title: "User Admin employee directories and history views added",
    summary:
      "User Admins can now browse employee directory records and review hierarchy and department change history in one place.",
    details: [
      "Added User Admin directory views for browsing employee records.",
      "Introduced an employee hierarchy change history view to track reporting-line updates over time.",
      "Added employee department audit history so department moves can be reviewed alongside hierarchy changes.",
      "Backend directory endpoints support the new browsing and history views.",
    ],
    area: "Platform",
  },
  {
    id: "2026-08-19-resource-health-question-notifications",
    date: "2026-08-19",
    title: "Resource Health question notifications added",
    summary:
      "Resource Health and Super Admins now receive an email when a new question is submitted for review.",
    details: [
      "Notifications are sent to active ResourceComplianceAdmin and SuperAdmin users.",
      "The email includes the submitted question and the person who submitted it.",
      "Questions remain saved even if email queuing is temporarily unavailable.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-08-18-sharepoint-account-purge-admin",
    date: "2026-08-18",
    title: "SharePoint Account Purge admin workflow added",
    summary:
      "User Admins can now run SharePoint account cleanup requests, choose whether to include OneDrive, and review report results in one place.",
    details: [
      "Added a dedicated User Admin area for SharePoint account operations.",
      "Supports dry-run requests before live access removal is performed.",
      "Added optional OneDrive handling for account cleanup scenarios.",
      "Purge history includes status, mode, timing, requester, and expandable report results.",
    ],
    area: "Platform",
  },
  {
    id: "2026-08-18-group-self-removal-guidance",
    date: "2026-08-18",
    title: "Group self-removal now provides clearer guidance",
    summary:
      "Leaving a Microsoft 365 group now uses the live group identity and explains when another owner must be assigned first.",
    details: [
      "Self-removal now works for live Graph groups that do not have a local resource record.",
      "Users receive a clear explanation when they are the only group owner.",
      "The operation avoids partially removing a user when Microsoft 365 ownership rules prevent leaving.",
      "The workflow now uses the live Microsoft 365 group identity so local resource records are not required.",
    ],
    area: "Platform",
  },
  {
    id: "2026-07-21-out-of-band-communications-platform",
    date: "2026-07-21",
    title: "Out-of-Band communications platform is now available",
    summary:
      "The OOB platform now supports event administration, participant readiness, emergency access, status updates, and acknowledgement workflows.",
    details: [
      "Added OOB event and participant administration with group-based targeting.",
      "Added break-glass and external participant access paths for emergency scenarios.",
      "Added contact verification, silent-mode testing, artifact handling, and event status updates.",
      "Administrators can review acknowledgements, audit history, and event lifecycle activity in-app.",
    ],
    area: "Platform",
  },
  {
    id: "2026-07-10-resource-compliance-helpful-votes-single-vote",
    date: "2026-07-10",
    title: "Community voting is now available for Help questions",
    summary:
      "You can now vote on Community Questions to surface the most useful topics and answers in Resource Health Help.",
    details: [
      "Introduced a community voting workflow for Resource Health community questions.",
      "Helpful button now becomes Voted after your vote is recorded.",
      "Each user can cast one active vote per question to keep rankings fair.",
      "Vote totals are returned from backend-confirmed results to keep counts reliable.",
    ],
    area: "Platform",
  },
  {
    id: "2026-07-10-resource-compliance-questions-admin",
    date: "2026-07-10",
    title: "Resource Health questions now have an admin management page",
    summary:
      "Admins can now review, edit, publish, answer, and remove Ask a Question records directly in-app.",
    details: [
      "Added a dedicated admin page at /admin/resource-compliance-questions.",
      "Introduced search and unanswered-only filtering for faster moderation workflows.",
      "Added edit and delete actions with modal-based form editing for main question fields.",
      "Connected the page to the Resource Health admin help APIs.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-07-07-resource-compliance-help-modal-v1",
    date: "2026-07-07",
    title: "Resource Health Help modal launched",
    summary:
      "A new Need Help entry point now opens a large Resource Health help modal with searchable FAQs and Community Questions.",
    details: [
      "Added a prominent Need Help button directly on the Resource Health page.",
      "Introduced a large help modal with two tabs: FAQs and Community Questions.",
      "Added collapse/expand behavior with Expand all and Collapse all controls.",
      "Applied category-based color badges for faster scanning of FAQ topics.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-07-07-resource-compliance-guided-tour-entry",
    date: "2026-07-07",
    title: "Guided tour can now be started from the page",
    summary:
      "Resource Health now includes a dedicated Take Tour button so users can start onboarding guidance on demand.",
    details: [
      "Added a Take Tour button beside Need Help for immediate in-page launch.",
      "Extended tour state management with explicit programmatic start support.",
      "Added lightweight close control in the tour popup for easier exit.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-07-07-resource-compliance-tour-visual-refresh",
    date: "2026-07-07",
    title: "Tour popup visual design refreshed",
    summary:
      "The guided tour popup now uses stronger visual hierarchy, improved spacing, and themed action styling for clearer focus.",
    details: [
      "Increased popup size and shadow depth to improve legibility and separation from page content.",
      "Updated accent treatments with a gold-toned panel style and matching border cues.",
      "Aligned primary button styling with app theme tokens for consistent action colors.",
      "Added a stable icon tile in the popup header for stronger visual identity.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-06-29-bulk-metadata-updates",
    date: "2026-06-29",
    title: "Bulk metadata update feature launched in Resources tab",
    summary:
      "Users can now efficiently update metadata for up to 10 resources at once with checkboxes, type-ahead autocomplete, and instant validation.",
    details: [
      "Added checkbox selection with max 10 resources limit and smart visibility (only resources with missing metadata are selectable).",
      "Implemented type-ahead autocomplete for Primary Owner, Secondary Owner, and Cost Center fields.",
      "Business Unit now displayed as color-coded pills (Corporation=blue, Energy=green, Power=cyan) with Department ID on separate line.",
      "Bulk update confirms metadata review status in SharePointSiteCollections automatically.",
      "State updates optimized to target only affected rows instead of full page reload.",
      "Success feedback via in-app modal with resource count confirmation.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-06-26-resource-compliance-dashboard-polish",
    date: "2026-06-26",
    title: "Resource Health dashboard redesigned with color-coded metrics and improved UX",
    summary:
      "Major visual polish and usability improvements to the Resource Health dashboard including color-coded health badges, redesigned layouts, and smarter visibility logic for managers vs. individual contributors.",
    details: [
      "Added color-coded health score badges: red (<50%), orange (50-69%), yellow (70-89%), green (≥90%).",
      "Redesigned Overview tab with three-column primary metrics (Resource Health, Total Resources, Total Storage) plus single-line Review timeline.",
      "Converted all storage displays from MB to GB with 1 decimal place across all tables and summaries.",
      "Right-aligned all numeric columns (Resource Count, Storage GB, Health, Due fields) for improved scannability.",
      "Redesigned My Resources profile header with image circle (80x80px), name, email, and resource count below.",
      "Hidden 'Recursive Health' metric for individual contributors (only shown when user has direct reports).",
      "Hidden person search UI in My Organization tab unless user is Super Admin.",
      "Hidden hierarchical sections (Levels below, Resources accountable, Storage accountable, Accountable Portfolio) when user has no direct reports.",
      "Enhanced missing field display with color-coded pills and humanized field names (stripped 'Ntid' suffix).",
      "Increased Review timeline size in Overview tab for better visibility.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-06-12-metadata-review-ux",
    date: "2026-06-12",
    title: "Metadata review workflow now integrated into save flow",
    summary:
      "Saving metadata now also confirms the review timestamp in one step, with smoother loading and auto-prompt behavior.",
    details: [
      "Save action now updates metadata and review tracking together.",
      "Metadata modal auto-opens when review metadata is missing.",
      "Resource details loading was improved with staged loading and fade-in.",
      "Metadata card now shows review status (who/when/next due).",
    ],
    area: "Frontend",
  },
  {
    id: "2026-06-12-metadata-review-tracking",
    date: "2026-06-12",
    title: "Metadata review tracking added",
    summary:
      "Platform now tracks who reviewed metadata, when it was reviewed, and when it is next due.",
    details: [
      "Added metadataLastReviewedBy, metadataLastReviewedDt, metadataNextReviewDt columns.",
      "Added metadata review confirmation endpoint for group routes.",
      "Added overdue-friendly index on metadataNextReviewDt for active rows.",
    ],
    area: "Backend",
  },
  {
    id: "2026-06-12-whats-new-surface",
    date: "2026-06-12",
    title: "In-app What's New experience introduced",
    summary:
      "Release notes are now visible in-app through a header modal and a dedicated What's New page.",
    details: [
      "Added top-right header button for quick update summaries.",
      "Added a dedicated /whats-new page for full update history.",
      "Both modal and page are powered by one shared data source.",
    ],
    area: "Frontend",
  },
  {
    id: "2026-05-22-sharepoint-archive-admin",
    date: "2026-05-22",
    title: "SharePoint Groups Admin and archive review workflows expanded",
    summary:
      "Admin tooling for group storage visibility and archive review operations was significantly expanded.",
    details: [
      "Added groups admin pages, storage tab enhancements, and archive review modal flows.",
      "Extended backend support for site storage visibility and archive review trigger paths.",
      "Commit refs: frontend 56798f5, backend de08b6d.",
    ],
    area: "Platform",
  },
  {
    id: "2026-05-20-reviews-platform-improvements",
    date: "2026-05-20",
    title: "Reviews platform matured with assignment and reporting workflows",
    summary:
      "Review lifecycle handling and related admin workflows were improved across frontend and backend services.",
    details: [
      "Frontend introduced major review system improvements and admin UX updates.",
      "Backend added linked-report and queueing support plus review data fixes.",
      "Commit refs: frontend 6129c74, backend 3020aed.",
    ],
    area: "Platform",
  },
  {
    id: "2026-04-23-resource-support-delete-hardening",
    date: "2026-04-23",
    title: "Resource support and deletion flows were hardened",
    summary:
      "Core support-request and delete-group behavior was tightened for reliability and activity audit completeness.",
    details: [
      "Fixed support and delete flows and improved activity logging behavior.",
      "Stabilized related modal and dashboard interactions in frontend.",
      "Commit refs: backend 21a3505, frontend 1cc8ac9.",
    ],
    area: "Platform",
  },
  {
    id: "2026-01-20-delegation-inbox-build-1",
    date: "2026-01-20",
    title: "Delegation Inbox Build 1 completed",
    summary:
      "Delegated work inbox launched with tabbed tracking for active, pending, and completed delegation states.",
    details: [
      "Added Delegated Work Inbox with four status tabs and live counts.",
      "Integrated side menu navigation and route support for delegations.",
      "Implemented loading, error, and responsive states for inbox workflows.",
    ],
    area: "Frontend",
  },
  {
    id: "2025-11-25-notifications-end-to-end",
    date: "2025-11-25",
    title: "Notifications became an end-to-end platform feature",
    summary:
      "Notifications gained complete frontend experience and backend API support for richer user messaging.",
    details: [
      "Implemented full notifications UI with themed master-detail layout.",
      "Added notification API support and event detail endpoint coverage.",
      "Commit refs: frontend 46a498d, backend c24c5ec.",
    ],
    area: "Platform",
  },
];
